import React, { useState, useEffect } from 'react';
import { Typography, Space, Button, Table, Select, Input, Breadcrumb, Spin } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import { showConfirmModal } from '../components/PopupModal';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import restaurantGuidesService from '../services/restaurantGuides';
import type { RestaurantGuide } from '../types/restaurant';
import dayjs from 'dayjs';
import { message } from '../utils/message';

const { Title, Text } = Typography;
const { Option } = Select;

interface RestaurantData {
  key: string;
  no: number;
  exposure: string;
  guideName: string;
  manager: string;
  registrationDate: string;
}

const Restaurants: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RestaurantData[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [exposureFilter, setExposureFilter] = useState('전체');
  const [searchValue, setSearchValue] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const columns: ColumnsType<RestaurantData> = [
    {
      title: 'No.',
      dataIndex: 'no',
      key: 'no',
      width: 80,
      align: 'center',
    },
    {
      title: '노출여부',
      dataIndex: 'exposure',
      key: 'exposure',
      width: 100,
      align: 'center',
    },
    {
      title: '가이드명',
      dataIndex: 'guideName',
      key: 'guideName',
      ellipsis: true,
    },
    {
      title: '관리자명',
      dataIndex: 'manager',
      key: 'manager',
      width: 120,
      align: 'center',
    },
    {
      title: '등록일',
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      width: 120,
      align: 'center',
    },
  ];

  // 데이터 로드 함수
  const loadData = async (page = currentPage, size = pageSize, exposure = exposureFilter, search = searchValue) => {
    try {
      setLoading(true);
      const response = await restaurantGuidesService.getGuides({
        page,
        limit: size,
        exposure: exposure !== '전체' ? exposure : undefined,
        search: search.trim() || undefined,
      });

      const transformedData: RestaurantData[] = response.guides.map((guide: RestaurantGuide, index: number) => ({
        key: guide.id.toString(),
        no: (page - 1) * size + index + 1,
        exposure: guide.exposure === 'show' ? '노출' : '비노출',
        guideName: guide.guideName,
        manager: guide.creator?.name || guide.creator?.username || guide.creator?.email || 'Unknown',
        registrationDate: dayjs(guide.createdAt).format('YYYY.MM.DD'),
      }));

      setData(transformedData);
      setTotal(response.pagination.totalItems);
    } catch (error: any) {
      console.error('데이터 로드 실패:', error);
      message.error('데이터를 불러오는데 실패했습니다.');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트시 데이터 로드
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 필터나 검색 변경시 데이터 다시 로드
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadData(1, pageSize, exposureFilter, searchValue);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exposureFilter, searchValue]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // 행 클릭 핸들러
  const handleRowClick = (record: RestaurantData) => {
    navigate(`/restaurants/detail/${record.key}`);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page: number, size?: number) => {
    const newPageSize = size || pageSize;
    setCurrentPage(page);
    setPageSize(newPageSize);
    loadData(page, newPageSize, exposureFilter, searchValue);
  };

  // 노출여부 필터 변경 핸들러
  const handleExposureFilterChange = (value: string) => {
    setExposureFilter(value);
  };

  // 검색 변경 핸들러
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  // 노출/비노출 토글 버튼 핸들러
  const handleExposureToggle = async (type: '노출' | '비노출') => {
    if (selectedRowKeys.length === 0) {
      message.warning('노출여부를 변경할 항목을 선택해주세요.');
      return;
    }

    try {
      const exposure = type === '노출' ? 'show' : 'hide';
      await restaurantGuidesService.updateExposure(selectedRowKeys as string[], exposure);
      message.success(type === "노출" ? "노출 처리 되었습니다." : "비노출 처리 되었습니다.");
      setSelectedRowKeys([]);
      loadData(); // 데이터 새로고침
    } catch (error: any) {
      console.error('노출여부 변경 실패:', error);
      const errorMessage = error?.response?.data?.message || '노출여부 변경 중 오류가 발생했습니다.';
      message.error(errorMessage);
    }
  };

  // 삭제 핸들러
  const handleDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('삭제할 항목을 선택해주세요.');
      return;
    }

    showConfirmModal({
      title: '삭제',
      content: '삭제한 콘텐츠는 복구할 수 없습니다.\n삭제하시겠습니까?',
      okText: '확인',
      okType: 'danger',
      cancelText: '취소',
      onConfirm: async () => {
        try {
          setDeleteLoading(true);
          await restaurantGuidesService.deleteGuides(selectedRowKeys as string[]);
          message.success("삭제가 완료되었습니다.");
          setSelectedRowKeys([]);
          loadData(); // 데이터 새로고침
        } catch (error: any) {
          console.error('삭제 실패:', error);
          const errorMessage = error?.response?.data?.message || '삭제 중 오류가 발생했습니다.';
          message.error(errorMessage);
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '라이프&컬쳐' },
          { title: '맛집 가이드' }
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <Title level={2} style={{ margin: '0 0 32px 0' }}>
        맛집 가이드
      </Title>

      {/* 필터 및 검색 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>노출여부</Text>
            <Select
              value={exposureFilter}
              onChange={handleExposureFilterChange}
              style={{ width: 120 }}
            >
              <Option value="전체">전체</Option>
              <Option value="노출">노출</Option>
              <Option value="비노출">비노출</Option>
            </Select>
          </div>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/restaurants/add')}>
          추가
        </Button>
      </div>

      {/* 구분선 */}
      <div style={{
        height: '1px',
        backgroundColor: '#303030',
        margin: '24px 0'
      }} />

      {/* 테이블 상단 정보 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Text>총 {total}건</Text>
          <Button
            onClick={handleDelete}
            disabled={selectedRowKeys.length === 0}
            danger
            size="middle"
          >
            삭제
          </Button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            onClick={() => handleExposureToggle('비노출')}
            disabled={selectedRowKeys.length === 0}
            size="middle"
          >
            비노출
          </Button>
          <Button
            onClick={() => handleExposureToggle('노출')}
            disabled={selectedRowKeys.length === 0}
            size="middle"
          >
            노출
          </Button>
          <Input
            placeholder="검색"
            suffix={<SearchOutlined style={{ color: '#a6a6a6' }} />}
            style={{
              width: 400,
              height: 40,
              borderRadius: 8,
              background: '#2a2a2a',
              border: '1px solid #404040',
              color: '#ffffff'
            }}
            onChange={handleSearchChange}
            value={searchValue}
            allowClear
          />
        </div>
      </div>

      {/* 테이블 */}
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        loading={loading}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' }
        })}
        pagination={{
          current: currentPage,
          total: total,
          pageSize: pageSize,
          showSizeChanger: true,
          showQuickJumper: false,
          pageSizeOptions: ['10', '20', '50'],
          style: { textAlign: 'center' },
          onChange: handlePageChange,
          onShowSizeChange: handlePageChange,
        }}
        style={{
          backgroundColor: '#1f1f1f',
        }}
        size="middle"
        locale={{
          emptyText: loading ? '로딩 중...' : '데이터가 없습니다.'
        }}
      />
    </Space>
  );
};

export default Restaurants;